<template>
  <section class="mx-auto py-6 md:py-14 md:w-full">
    <div class="container mx-auto">
      <h2 class="text-center md:text-left section-heading mb-8">
        What Our Employees Say about Us
      </h2>
    </div>

    <div class="relative container mx-auto">
      <!-- CUSTOM NAVIGATION -->
      <div
        class="hidden arrow-buttons lg:flex justify-between absolute top-[50%] w-[106%] left-[-3%]"
      >
        <button :disabled="isBeginning" @click.stop="slider.slidePrev()">
          <nuxt-icon name="icon-park_arrow-left" filled />
        </button>
        <button :disabled="isLast" @click.stop="slider.slideNext()">
          <nuxt-icon name="icon-park_arrow-right" filled />
        </button>
      </div>

      <template v-if="!isLoading">
        <Swiper
          :modules="[SwiperNavigation, SwiperPagination]"
          :slides-per-group="1"
          :slides-per-view="1"
          :space-between="40"
          :grab-cursor="false"
          :pagination="pagination"
          :long-swipes-ms="800"
          :long-swipes-ratio="1"
          :breakpoints="{
            '640': {
              slidesPerGroup: 2,
              slidesPerView: 2,
              spaceBetween: 20
            },
            '768': {
              slidesPerGroup: 2,
              slidesPerView: 2,
              spaceBetween: 40
            },
            '1024': {
              slidesPerGroup: 3,
              slidesPerView: 3,
              spaceBetween: 40
            }
          }"
          class=""
          @swiper="onSwiper"
        >
          <SwiperSlide
            v-for="(slide, index) in reviewsData"
            :key="slide.attributes?.id"
            class="my-auto carousel-opacity border border-[#E8E8E8] relative rounded-md"
          >
            <div class="flex gap-4 items-end px-5 pt-5">
              <NuxtImg
                format="webp"
                srcset=""
                :src="slide.attributes?.image?.data?.[0]?.attributes?.url"
                :alt="slide.attributes?.name"
                :title="slide.attributes?.name"
                loading="lazy"
                class="mx-auto absolute -top-10 z-[1] w-[100px] rounded"
              />
              <div class="w-full ml-28">
                <p class="text-lg text-bw-900 font-medium">
                  {{ slide.attributes?.name }}
                </p>
                <p class="text-sm text-bw-800">{{ slide.attributes?.title }}</p>
              </div>
            </div>
            <div class="px-5 pt-4">
              <p class="text-base font-medium text-bw-900 mb-0">
                {{ slide.attributes?.subtitle }}
              </p>
            </div>
            <div
              ref="testimony"
              class="px-5 pt-2 text-base text-bw-800 ease-in-out duration-200 transition-all testimony relative"
              :class="
                testimonyIndex.includes(index) ? 'is-open' : 'line-clamp-4'
              "
              v-html="slide.attributes?.testimony"
            ></div>
            <div class="px-5 pb-5">
              <button
                v-if="testimonyIndex.includes(index)"
                class="text-primary-primary underline text-base"
                @click="showLessTesimony(index)"
              >
                See Less
              </button>
              <button
                v-else-if="
                  !testimonyIndex.includes(index) &&
                  Math.ceil(testimony?.[index].offsetHeight / 24) >= 5
                "
                class="text-primary-primary underline text-base"
                @click="showMoreTestimony(index)"
              >
                Read More
              </button>
            </div>
          </SwiperSlide>
        </Swiper>
      </template>
    </div>
  </section>
</template>

<script setup>
const slider = ref(null)
const testimony = ref(null)

const onSwiper = swiper => {
  slider.value = swiper
}

const testimonyIndex = ref([])

const isBeginning = computed(() => {
  return slider?.value?.isBeginning
})
const isLast = computed(() => {
  return slider?.value?.isEnd
})

const isLoading = ref(true)

const reviewsData = ref([])

const getReviewsData = async () => {
  const { data, error, pending } = await useSuitmediaApiData(
    `/api/testimonies`,
    {
      query: {
        'fields[0]': 'id',
        'fields[1]': 'name',
        'fields[2]': 'title',
        'fields[3]': 'testimony',
        'fields[4]': 'subtitle',
        populate: 'image'
      }
    }
  )

  isLoading.value = pending.value

  if (error.value) {
    throw createError({
      statusCode: 404,
      message: `Testimonies does not exist`
    })
  }

  return data.value
}

const reviews = await getReviewsData()
reviewsData.value = reviews.data

const pagination = {
  clickable: true,
  renderBullet: function (index, className) {
    return `<span class="${className} ${index}"></span>`
  }
  // modules: [SwiperPagination]
}

const showMoreTestimony = index => {
  testimonyIndex.value.push(index)
}
const showLessTesimony = index => {
  testimonyIndex.value = testimonyIndex.value.filter(val => val !== index)
}
</script>

<style lang="scss" scoped>
:deep(.swiper) {
  padding-top: 50px;
}
.arrow-buttons {
  button:disabled,
  button[disabled] {
    opacity: 0.4;
  }
}
:deep(.swiper-wrapper) {
  // height: 50vh;
  padding: 0 0 60px;
}
:deep(.swiper-slide) {
  height: auto;
  margin-top: 0;
  margin-bottom: auto;
  transition:
    opacity 0.5s ease,
    transform 0.5s ease;
}

:deep(.swiper-pagination) {
  display: flex;
  align-items: center;
  justify-content: center;
}

:deep(.swiper-pagination-bullet) {
  width: 12px;
  height: 12px;
  background: white;
  opacity: 1;

  border: 1px solid #ff6700;
}
:deep(.swiper-pagination-bullet-active) {
  width: 22px;
  height: 22px;
  background-color: #ff6700;
  border: 6px solid #fee0cc;
  opacity: 1;

  // border: 1px solid #fee0cc;
}

:deep(.nuxt-icon) svg {
  width: 2em;
  height: 2em;
}

.testimony {
  max-height: 104px;
  overflow: hidden;
  transition: all 0.5s ease-in-out;
  position: relative;

  &.is-open {
    max-height: 1000px;
  }

  :deep(p) {
    position: relative;
    z-index: 1;
  }
  &::before {
    content: '“';
    font-size: 96px;
    color: rgba(232, 232, 232, 1);
    position: absolute;
    top: 26px;
    left: 7px;
    z-index: 0;
  }
}

.height-slide-enter-active {
  p {
    transition: all 0.3s ease;
  }
  // max-height: 400px;
}

.height-slide-leave-active {
  p {
    transition: all 0.3s ease;
  }
  // max-height: 400px;
}

.height-slide-enter-from {
  p {
    opacity: 0;
    transform: translateY(-50px);
  }
}
.height-slide-leave-to {
  p {
    opacity: 0;
    transform: translateY(-30px);
  }
}
</style>
